import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Button, CircularProgress, InputAdornment, MenuItem, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import WarningPage from './WarningPage';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import { UpdateButton } from '../../components/mui/Buttons/Buttons';
import apiRequest from '../../api/request';
import useWarningMonth from './useWarningMonth';
import WorkingMonthBar from './WorkingMonthBar';
import EffectiveFromField from './EffectiveFromField';
import AppliesCell from './AppliesCell';
import ActiveSwitch from './ActiveSwitch';
import { defaultEffectiveMonth, emptyEffective, startsWithin } from './effectiveWindow';
import { RULES, numberField, validateNumber } from './thresholdValidation';
import MetricDefinitionNote from './MetricDefinitionNote';
import useConfigAccess from './useConfigAccess';

// MAU's threshold screen - DEPARTMENT-wise. Each department sets "a CRO of this department should be handling at least N
// active students"; the MAU results compare every CRO's "Active students (at month end)" with the number of THEIR department
// and flag the CRO when it is lower. (It used to be one global number compared with the company-wide total, which could
// never warn and appeared nowhere on the CRO table.) The warning is always "below"; the number is a whole number of students.
// Same dated versions as the other screens: Applies from / until, switching on and off from the working month.
const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const emptyForm = { department_id: '', threshold_value: '', is_active: true };

const MauConfig = () => {
  const [month, setMonth] = useWarningMonth(); // the month being worked on - passed on to the results screen
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm, ...emptyEffective() });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [submitted, setSubmitted] = useState(false); // a blank number only complains after the first Save attempt
  const [toggling, setToggling] = useState({}); // rows whose switch request is still in flight - one at a time per row
  const fetchSeq = useRef(0); // each load gets a number; only the newest may update the screen

  const fetchRows = ({ silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setRowsLoading(true); // a silent reload (after a switch) keeps the table on screen - no flash
    // apiRequest resolves even on a failed call (src/api/request.js) - the status check is what makes a failure visible
    return apiRequest({ url: `warning-dashboard/mau-thresholds?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (seq !== fetchSeq.current) return; // a newer load replaced this one
        if (res?.status !== 'success') {
          setRowsError(res?.message || 'Could not load the MAU thresholds');
          return;
        }
        setRows(res?.data?.rows || []);
        setRowsError(null);
      })
      .catch((err) => {
        if (seq === fetchSeq.current) setRowsError(err?.response?.data?.message || 'Could not load the MAU thresholds');
      })
      .finally(() => {
        if (seq === fetchSeq.current) setRowsLoading(false);
      });
  };

  useEffect(() => {
    fetchRows();
  }, [month]); // reload when the working month changes

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, department_id: defaultDepartment(DEPARTMENTS), ...emptyEffective() }); // the month to apply from is read fresh each time the form opens
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      department_id: row.department_id,
      threshold_value: String(Number(row.threshold_value)),
      is_active: true, // opening an edit on a switched-off threshold means "use it" - switch it off again below if not
      ...emptyEffective(),
    });
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const thresholdField = numberField(form.threshold_value, 'Threshold', RULES.wholeNumber, submitted);
  // Several versions of one department's threshold are normal (a change from a later month, a range), so only an ACTIVE one
  // that begins inside the months being added is a conflict; one that began earlier is simply cut back. The server decides edits.
  const duplicateError = form.department_id && !editingId && rows.find((r) => Number(r.department_id) === Number(form.department_id) && r.is_active && startsWithin(r, form))
    ? `${departmentName(form.department_id)} already has a threshold that starts in the chosen month or later - edit it, or choose a later month`
    : '';
  const departmentError = submitted && !form.department_id ? 'Choose a department' : '';

  const handleSave = () => {
    setSubmitted(true);
    if (!form.department_id || duplicateError || validateNumber(form.threshold_value, 'Threshold', RULES.wholeNumber)) return;
    setSaving(true);
    const request = editingId
      ? apiRequest({ url: `warning-dashboard/mau-thresholds/${editingId}`, method: 'put', data: form })
      : apiRequest({ url: 'warning-dashboard/mau-thresholds', method: 'post', data: form });
    request
      .then((res) => {
        if (res?.status !== 'success') {
          setSaveError(res?.message || 'Could not save this threshold');
          return;
        }
        setFormOpen(false);
        return fetchRows();
      })
      .catch((err) => setSaveError(err?.response?.data?.message || 'Could not save this threshold'))
      .finally(() => setSaving(false));
  };

  const handleToggleActive = (row) => {
    if (toggling[row.id]) return;
    setToggling((t) => ({ ...t, [row.id]: true }));
    const nextActive = !row.is_active;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r))); // optimistic, undone on failure
    const undo = () => setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
    apiRequest({ url: `warning-dashboard/mau-thresholds/${row.id}/active`, method: 'patch', data: { is_active: nextActive, effective_month: defaultEffectiveMonth() } })
      .then((res) => {
        if (res?.status !== 'success') undo();
        else fetchRows({ silent: true }); // a switch can start a new version from the working month
      })
      .catch(undo)
      .finally(() => setToggling((t) => { const next = { ...t }; delete next[row.id]; return next; }));
  };

  const { defaultDepartment } = useConfigAccess(); // an Add form starts on the person's own department (they can change it)

  return (
    <WarningPage
      title="MAU Threshold Configuration"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard/mau?month=${month.format('YYYY-MM-DD')}`} variant="outlined">View Results</Button>
          {!formOpen && (
            <Button startIcon={<Icon icon="mynaui:config-solid" width="20" />} variant="contained" onClick={openCreate}>Add Threshold</Button>
          )}
        </Box>
      }
    >
      <WorkingMonthBar month={month} setMonth={setMonth} />
      <Box sx={{ mb: 3 }}>
        <MetricDefinitionNote
          metricKey="mau"
          text="Set per department: every CRO of that department is checked against its department's number. A CRO handling fewer ACTIVE STUDENTS (at the end of the month) than that is flagged on the MAU results."
        />
      </Box>

      {formOpen && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{editingId ? 'Edit Threshold' : 'Add Threshold'}</Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
              One number per department: a CRO of the department who is handling fewer active students than this is flagged.
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                select label="Department" value={form.department_id} disabled={!!editingId}
                onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                sx={{ minWidth: 220, flex: 1 }} error={!!(departmentError || duplicateError)} helperText={departmentError || duplicateError || undefined}
              >
                {DEPARTMENTS.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
              <TextField
                label="A CRO should handle at least" value={form.threshold_value}
                onChange={(e) => setForm({ ...form, threshold_value: e.target.value })}
                sx={{ width: 260 }} {...thresholdField.props}
                InputProps={{ endAdornment: <InputAdornment position="end">students</InputAdornment> }}
              />
            </Box>

            <EffectiveFromField form={form} setForm={setForm} />

            <ActiveSwitch form={form} setForm={setForm} offHint="While off, no CRO of this department is flagged." />

            {saveError && <Typography color="error.main" variant="body2">{saveError}</Typography>}

            <Box display="flex" gap={1}>
              <UpdateButton action={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</UpdateButton>
              <Button variant="text" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {rowsLoading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary">Loading thresholds...</Typography>
        </Box>
      )}

      {!rowsLoading && rowsError && <RetryNotice message={rowsError} onRetry={fetchRows} />}

      {!rowsLoading && !rowsError && rows.length === 0 && (
        <Typography color="text.secondary">No MAU thresholds configured yet. Use "Add Threshold" to set one for a department.</Typography>
      )}

      {!rowsLoading && !rowsError && rows.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>A CRO should handle at least</strong></TableCell>
                <TableCell><strong>Applies</strong></TableCell>
                <TableCell><strong>Active</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{departmentName(row.department_id)}</TableCell>
                  <TableCell>{Number(row.threshold_value).toLocaleString()} students</TableCell>
                  <TableCell><AppliesCell row={row} month={month} /></TableCell>
                  <TableCell>
                    <Switch checked={!!row.is_active} disabled={!!toggling[row.id]} onChange={() => handleToggleActive(row)} />
                  </TableCell>
                  <TableCell><UpdateButton size="small" action={() => openEdit(row)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </WarningPage>
  );
};

export default MauConfig;
